import { useState } from 'react';
import { Popover, Button, TextInput } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useAddBookingToOrder } from '@/hooks/api-hooks';
import { notify } from '@/lib/notify';

interface Props {
    serviceBookingId: number;
}

export default function AddBookingToOrderPopover({ serviceBookingId }: Props) {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);
    const [orderId, setOrderId] = useState('');
    const addBookingMutation = useAddBookingToOrder();

    const handleSave = async () => {
        const parsedId = parseInt(orderId, 10);
        if (isNaN(parsedId)) {
            notify.error(t('error'), 'not_valid_id');
            return;
        }

        try {
            await addBookingMutation.mutateAsync({
                serviceOrderId: parsedId,
                serviceBookingId
            });
            setOpened(false);
            setOrderId('');
            notify.success(t('success'), t('added'));
        } catch (error) {
            notify.error(t('error'), t('error'));
        }
    };

    return (
        <Popover opened={opened} onChange={setOpened} position="bottom" withArrow shadow="md" width={220} trapFocus>
            <Popover.Target>
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpened((o) => {
                            if (!o) setOrderId('');
                            return !o;
                        });
                    }}
                    className="inline-flex items-center gap-1 border border-dashed border-gray-200 hover:border-purple-400 hover:bg-purple-50 text-gray-400 hover:text-purple-700 transition-colors cursor-pointer px-1.5 py-0.5 text-[10px] font-semibold rounded"
                >
                    <IconPlus size={10} />
                    {t('order')}
                </div>
            </Popover.Target>
            <Popover.Dropdown p="xs" onClick={(e) => e.stopPropagation()}>
                <div className="text-xs font-semibold mb-2 text-gray-500 px-1">
                    {t('service_order')}
                </div>
                <div className="flex flex-col gap-2">
                    <TextInput
                        size="xs"
                        placeholder="ID"
                        type="number"
                        value={orderId}
                        onChange={(e) => setOrderId(e.currentTarget.value)}
                        data-autofocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSave();
                            }
                        }}
                    />
                    <Button
                        size="xs"
                        color="black"
                        loading={addBookingMutation.isPending}
                        onClick={handleSave}
                        fullWidth
                    >
                        {t('save')}
                    </Button>
                </div>
            </Popover.Dropdown>
        </Popover>
    );
}

